/* eslint-disable react/jsx-key */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { fetchData, getSuggestedPeriodByDataRecency } from '../../utils';
import { useSelector } from 'react-redux';
import { CARD_INFO_TEMPLATE, periods } from '../../constants';
import Loading from '../../components/Loading';
import DisplayModeToggle from '../../components/DisplayModeToggle';
import { Filters } from './Filters';
import Table from '../../components/Tables/Table';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import PeriodButtons from '../../components/PeriodButtons';
import { format, startOfWeek } from 'date-fns';
import { colorsDashboard } from '../../constants/defaultOptionsChart';
import {
  getAverageDeveloperCostFromSource,
  parseNumericInput,
  shouldFetchAverageDeveloperCostFromEndpoint
} from '../../utils/companyConfigurations';
import { CardContainer } from './CardContainer';
import { sendRequest } from '../../utils/dateHelpers';

const DISPLAY_MODES = {
  REAL_EFFORT: 'git_effort',
  INVESTMENT: 'investment'
};

const getAverageDeveloperCost = userInfo => {
  const directCost = getAverageDeveloperCostFromSource(userInfo);

  if (directCost > 0) return directCost;

  return getAverageDeveloperCostFromSource(userInfo?.user);
};

const convertEffortToInvestment = (value, averageCost) => {
  const effortValue = parseNumericInput(value);
  return Math.round(
    ((Number.isFinite(effortValue) ? effortValue : 0) / 60) * averageCost
  );
};

const Overview = () => {
  const { userInfo = {} } = useSelector(state => state);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [optionsChart, setOptionsChart] = useState({});
  const [optionsChart2, setOptionsChart2] = useState({});
  const [cardInfo, setCardInfo] = useState(Object.values(CARD_INFO_TEMPLATE));
  const [hiddenCategorySeries, setHiddenCategorySeries] = useState({});

  const [activePeriod, setActivePeriod] = useState(periods.MONTH);
  const [activePeriodCategory, setActivePeriodCategory] = useState(
    periods.MONTH
  );
  const mainPeriodManualOverrideRef = useRef(false);
  const mainPeriodAutoResolvedRef = useRef(false);
  const categoryPeriodManualOverrideRef = useRef(false);
  const categoryPeriodAutoResolvedRef = useRef(false);
  const categoryChartRef = useRef(null);
  const [responseData, setResponseData] = useState();
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODES.REAL_EFFORT);
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );
  const [fallbackAverageDeveloperCost, setFallbackAverageDeveloperCost] =
    useState(0);
  const roleBasedAverageDeveloperCost = getAverageDeveloperCost(userInfo);
  const averageDeveloperCost =
    roleBasedAverageDeveloperCost > 0
      ? roleBasedAverageDeveloperCost
      : fallbackAverageDeveloperCost;
  const hasClientSideInvestmentRate = averageDeveloperCost > 0;
  const shouldRequestServerCalculatedInvestment =
    displayMode === DISPLAY_MODES.INVESTMENT && !hasClientSideInvestmentRate;

  const {
    dashboardData: { authors }
  } = userInfo;

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchAverageDeveloperCost = async () => {
      try {
        const response = await sendRequest('/company-configurations', 'GET');
        const fallbackCost = getAverageDeveloperCostFromSource(response);

        if (fallbackCost > 0) {
          setFallbackAverageDeveloperCost(fallbackCost);
        }
      } catch (error) {
        console.error('Failed to load fallback average developer cost', error);
      }
    };

    if (
      roleBasedAverageDeveloperCost > 0 ||
      !shouldFetchAverageDeveloperCostFromEndpoint(
        userInfo?.role,
        userInfo?.user?.role
      )
    ) {
      return;
    }

    fetchAverageDeveloperCost();
  }, [roleBasedAverageDeveloperCost, userInfo?.role, userInfo?.user?.role]);

  useEffect(() => {
    if (optionsChart2?.series?.length) {
      setHiddenCategorySeries({});
    }
  }, [optionsChart2]);

  const handleChangeTime = perTemp => {
    mainPeriodManualOverrideRef.current = true;
    mainPeriodAutoResolvedRef.current = true;
    setActivePeriod(perTemp);
  };

  const handleChangeTimeCategory = perTemp => {
    categoryPeriodManualOverrideRef.current = true;
    categoryPeriodAutoResolvedRef.current = true;
    setActivePeriodCategory(perTemp);
  };

  const handleCategoryLegendToggle = index => {
    const chart = categoryChartRef.current?.chart;
    if (!chart) return;
    const series = chart.series?.[index];
    if (!series) return;
    const nextVisible = !series.visible;
    series.setVisible(nextVisible, false);
    chart.redraw();
    setHiddenCategorySeries(prev => ({
      ...prev,
      [index]: !nextVisible
    }));
  };

  const handleFilters = filtersTemp => {
    mainPeriodManualOverrideRef.current = false;
    mainPeriodAutoResolvedRef.current = false;
    categoryPeriodManualOverrideRef.current = false;
    categoryPeriodAutoResolvedRef.current = false;
    setFilters(filtersTemp);
  };

  const generateDateFormat = (groupBy, date) => {
    let groupKey;
    if (groupBy === 'day') {
      groupKey = format(date, 'yyyy-MM-dd');
    } else if (groupBy === 'week') {
      const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 });
      groupKey = format(startOfWeekDate, 'yyyy-MM-dd');
    } else if (groupBy === 'month') {
      groupKey = format(date, 'yyyy-MM-01');
    } else if (groupBy === 'year') {
      groupKey = format(date, 'yyyy');
    } else if (groupBy === 'total') {
      groupKey = 'total';
    }

    return groupKey;
  };

  const sumDataByPeriod = (data, groupBy) => {
    if (groupBy === 'day') return data;
    const result = {};

    Object.values(data).forEach(entry => {
      const date = new Date(entry.date);

      const keys = Object.keys(entry);
      const groupKey = generateDateFormat(groupBy, date);

      if (!result[groupKey]) {
        result[groupKey] = keys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});
      }

      keys.forEach(key => {
        if (typeof entry[key] === 'number') {
          result[groupKey][key] += entry[key];
        }
      });
    });

    return result;
  };

  const multipleChart = dataTemp => {
    const chartTextColor = isDarkMode ? '#cbd5e1' : '#334155';
    const chartMutedTextColor = isDarkMode ? '#94a3b8' : '#64748b';
    const chartGridColor = isDarkMode ? '#334155' : '#e2e8f0';
    const data = sumDataByPeriod(dataTemp, activePeriod);

    const categories = Object.keys(data);
    const insertions = categories.map(date => data[date].insertions);
    const deletions = categories.map(date => data[date].deletions);
    const gitEffort = categories.map(date => {
      const effortValue = data[date].git_effort;
      if (
        displayMode === DISPLAY_MODES.INVESTMENT &&
        hasClientSideInvestmentRate
      ) {
        return convertEffortToInvestment(effortValue, averageDeveloperCost);
      }
      return effortValue;
    });
    const commits = categories.map(date => data[date].commit);

    const options = {
      chart: {
        type: 'column',
        zoomType: 'x',
        height: 500
      },
      title: {
        text: ' '
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          color: chartTextColor
        },
        itemHoverStyle: {
          color: isDarkMode ? '#ffffff' : '#0f172a'
        }
      },
      xAxis: {
        categories: categories,
        title: {
          text: ''
        },
        labels: {
          style: {
            color: chartMutedTextColor
          }
        },
        lineColor: chartGridColor,
        tickColor: chartGridColor
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
        borderColor: chartGridColor,
        style: {
          color: chartTextColor
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal'
        }
      },
      credits: {
        enabled: false
      },
      yAxis: [
        {
          title: {
            text: 'Insertions / Deletions',
            style: {
              color: chartTextColor
            }
          },
          labels: {
            formatter: function () {
              return this.value;
            },
            style: {
              color: chartMutedTextColor
            }
          },
          gridLineColor: chartGridColor,
          opposite: false,
          stackLabels: {
            enabled: false
          },
          stacking: 'normal'
        },
        {
          title: {
            text:
              displayMode === DISPLAY_MODES.INVESTMENT
                ? 'Estimated Cost Without AI ($)'
                : 'Baseline Effort (REV) ',
            style: {
              color: chartTextColor
            }
          },
          labels: {
            formatter: function () {
              return this.value;
            },
            style: {
              color: chartMutedTextColor
            }
          },
          gridLineColor: chartGridColor,
          opposite: true
        },
        {
          title: {
            text: 'Commit',
            style: {
              color: chartTextColor
            }
          },
          labels: {
            formatter: function () {
              return this.value;
            },
            style: {
              color: chartMutedTextColor
            }
          },
          gridLineColor: chartGridColor,
          opposite: true
        }
      ],
      series: [
        {
          name: 'Insertions',
          data: insertions,
          color: '#5dc48b',
          yAxis: 0,
          stack: 'insertions' // Stacked group name
        },
        {
          name: 'Deletions',
          data: deletions,
          color: '#e74b71',
          yAxis: 0,
          stack: 'insertions' // Same stack name as Insertions
        },
        {
          name:
            displayMode === DISPLAY_MODES.INVESTMENT
              ? 'Estimated Cost Without AI ($)'
              : 'Baseline Effort (REV)',
          data: gitEffort,
          color: '#2caffe',
          type: 'spline', // Spline chart type
          yAxis: 1
        },
        {
          name: 'Commit',
          data: commits,
          color: '#544fc5',
          type: 'spline', // Spline chart type
          yAxis: 2
        }
      ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 640
            },
            chartOptions: {
              chart: {
                height: 360
              },
              xAxis: {
                labels: {
                  rotation: -45
                }
              },
              yAxis: [
                {
                  title: {
                    text: ''
                  }
                },
                {
                  title: {
                    text: ''
                  }
                },
                {
                  title: {
                    text: ''
                  }
                }
              ]
            }
          }
        ]
      }
    };

    setOptionsChart(options);
  };

  const multipleChartCategories = dataTemp => {
    const chartTextColor = isDarkMode ? '#cbd5e1' : '#334155';
    const chartMutedTextColor = isDarkMode ? '#94a3b8' : '#64748b';
    const chartGridColor = isDarkMode ? '#334155' : '#e2e8f0';
    const data = sumDataByPeriod(dataTemp, activePeriodCategory);

    const categories = Object.keys(data);
    const findData = Object.keys(data?.[categories[0]]).filter(
      a => a !== 'date'
    );

    const series = findData.map((category, index) => ({
      name: category,
      data: categories.map(date => {
        const value = data[date][category] || 0;
        return displayMode === DISPLAY_MODES.INVESTMENT &&
          hasClientSideInvestmentRate
          ? convertEffortToInvestment(value, averageDeveloperCost)
          : value;
      }),
      color: colorsDashboard[index],
      yAxis: 0,
      type: 'column',
      stack: 'Bug_Fixes'
    }));

    const options = {
      chart: {
        type: 'column',
        zoomType: 'x',
        height: 500
      },
      title: {
        text: ' '
      },
      legend: {
        enabled: false
      },
      xAxis: {
        categories: categories,
        title: {
          text: ''
        },
        labels: {
          style: {
            color: chartMutedTextColor
          }
        },
        lineColor: chartGridColor,
        tickColor: chartGridColor
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
        borderColor: chartGridColor,
        style: {
          color: chartTextColor
        },
        formatter: function () {
          return `<b>${this.series.name}</b><br/>${this.x}: <b>${Math.round(this.y).toLocaleString()}</b>`;
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal'
        }
      },
      credits: {
        enabled: false
      },
      yAxis: [
        {
          title: {
            text:
              displayMode === DISPLAY_MODES.INVESTMENT
                ? 'Estimated Cost Without AI by Category ($)'
                : 'Categories Effort',
            style: {
              color: chartTextColor
            }
          },
          labels: {
            formatter: function () {
              return this.value;
            },
            style: {
              color: chartMutedTextColor
            }
          },
          gridLineColor: chartGridColor,
          opposite: false,
          stackLabels: {
            enabled: false
          },
          stacking: 'normal'
        }
      ],
      series: series,
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 640
            },
            chartOptions: {
              chart: {
                height: 360
              },
              legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom'
              },
              xAxis: {
                labels: {
                  rotation: -45
                }
              },
              yAxis: [
                {
                  title: {
                    text: ''
                  }
                }
              ]
            }
          }
        ]
      }
    };

    setOptionsChart2(options);
  };

  const getData = async () => {
    try {
      setLoading(true);
      const responseDataTemp = await fetchData(
        {
          ...filters,
          operation: shouldRequestServerCalculatedInvestment
            ? DISPLAY_MODES.INVESTMENT
            : filters.operation
        },
        null,
        '/overview'
      );
      setResponseData(responseDataTemp);
    } catch (error) {
      setLoading(false);
      setResponseData([]);
      console.error('Error sending POST request:', error);
    }
  };

  useEffect(() => {
    if (filters.dateRange) {
      getData();
    }
  }, [filters, shouldRequestServerCalculatedInvestment]);

  const dataCreate = () => {
    if (responseData) {
      setLoading(false);
      if (
        !mainPeriodManualOverrideRef.current &&
        !mainPeriodAutoResolvedRef.current
      ) {
        const suggestedMainPeriod = getSuggestedPeriodByDataRecency(
          Object.keys(responseData?.chart2 || {})
        );
        mainPeriodAutoResolvedRef.current = true;

        if (suggestedMainPeriod !== activePeriod) {
          setActivePeriod(suggestedMainPeriod);
        }
      }

      if (
        !categoryPeriodManualOverrideRef.current &&
        !categoryPeriodAutoResolvedRef.current
      ) {
        const suggestedCategoryPeriod = getSuggestedPeriodByDataRecency(
          Object.keys(responseData?.chart3 || {})
        );
        categoryPeriodAutoResolvedRef.current = true;

        if (suggestedCategoryPeriod !== activePeriodCategory) {
          setActivePeriodCategory(suggestedCategoryPeriod);
        }
      }

      const newObj = [];
      const cardData = {
        ...responseData.cardData
      };
      if (
        displayMode === DISPLAY_MODES.INVESTMENT &&
        hasClientSideInvestmentRate &&
        cardData?.gitEfforts?.count !== undefined
      ) {
        cardData.gitEfforts = {
          ...cardData.gitEfforts,
          label: 'Estimated Cost Without AI ($)',
          count: convertEffortToInvestment(
            cardData.gitEfforts.count,
            averageDeveloperCost
          )
        };
      } else if (cardData?.gitEfforts) {
        cardData.gitEfforts = {
          ...cardData.gitEfforts,
          label: 'Baseline Effort (Without AI)'
        };
      }
      Object.keys(CARD_INFO_TEMPLATE).map(key => {
        newObj.push({ ...CARD_INFO_TEMPLATE[key], ...cardData[key] });
      });

      setCardInfo([...newObj]);

      const transformedData = Object.entries(responseData.chart1).reduce(
        (acc, [key, value]) => {
          acc[key] = {
            ...value,
            author_name: authors?.[value.author_uid]?.label || value.author_uid
          };
          if (
            displayMode === DISPLAY_MODES.INVESTMENT &&
            hasClientSideInvestmentRate
          ) {
            acc[key].git_effort = convertEffortToInvestment(
              value.git_effort || 0,
              averageDeveloperCost
            );
            acc[key].avg_git_effort = convertEffortToInvestment(
              value.avg_git_effort || 0,
              averageDeveloperCost
            );
          }
          return acc;
        },
        {}
      );

      setTableData(Object.values(transformedData));
      multipleChart(responseData.chart2);
      multipleChartCategories(responseData.chart3);
    }
  };

  useEffect(() => {
    if (responseData?.chart1) {
      dataCreate();
    } else {
      setCardInfo(Object.values(CARD_INFO_TEMPLATE));
      setTableData([]);
      setOptionsChart({});
      setOptionsChart2({});

      setLoading(false);
    }
  }, [
    responseData,
    activePeriod,
    activePeriodCategory,
    displayMode,
    averageDeveloperCost,
    isDarkMode
  ]);

  const categorySeries = optionsChart2?.series ?? [];

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-3">
          <Filters
            userInfo={userInfo}
            onChange={handleFilters}
            isHidden={['operation']}
            showContextSummary={true}
            summaryCategory="overview"
            summaryMetric={displayMode}
            afterViewType={
              <DisplayModeToggle
                value={displayMode}
                onChange={setDisplayMode}
              />
            }
          />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6 2xl:mt-7.5 2xl:gap-7.5">
        <div className="col-span-12">
          <CardContainer cardInfo={cardInfo} loading={loading} />
        </div>

        <div className="col-span-12 min-w-0 overflow-hidden rounded-sm border border-stroke bg-white px-4 pb-5 pt-6 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-6 sm:pt-7.5 xl:col-span-6 xl:px-7.5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex w-full flex-wrap gap-3 sm:gap-5">
              <div className="flex w-full">
                <span className="mt-1 mr-2 flex h-4 w-full max-w-4 items-center justify-center rounded-full border border-primary">
                  <span className="block h-2.5 w-full max-w-2.5 rounded-full bg-primary"></span>
                </span>
                <div className="w-full">
                  <p className="font-semibold text-primary">
                    Development Activity by Period
                  </p>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {filters?.dateRange?.startDate} -
                    {filters?.dateRange?.endDate}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex w-full justify-start lg:max-w-45 lg:justify-end">
              <div className="flex flex-wrap gap-2 justify-end">
                <PeriodButtons
                  activePeriod={activePeriod}
                  handleChangeTime={handleChangeTime}
                />
              </div>
            </div>
          </div>
          <div className="mt-6 min-w-0 sm:mt-8">
            <div className="w-full min-w-0 max-w-full overflow-hidden">
              {loading ? (
                <Loading type="chart" />
              ) : (
                <HighchartsReact
                  containerProps={{ className: 'overview-chart-host' }}
                  highcharts={Highcharts}
                  options={optionsChart}
                />
              )}
            </div>
          </div>
        </div>

        <div className="col-span-12 min-w-0 overflow-hidden rounded-sm border border-stroke bg-white px-4 pb-5 pt-6 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-6 sm:pt-7.5 xl:col-span-6 xl:px-7.5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex w-full flex-wrap gap-3 sm:gap-5">
              <div className="flex w-full">
                <span className="mt-1 mr-2 flex h-4 w-full max-w-4 items-center justify-center rounded-full border border-primary">
                  <span className="block h-2.5 w-full max-w-2.5 rounded-full bg-primary"></span>
                </span>
                <div className="w-full">
                  <p className="font-semibold text-primary">
                    Development Efforts by Category Over Time
                  </p>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {filters?.dateRange?.startDate} -
                    {filters?.dateRange?.endDate}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex w-full justify-start lg:max-w-45 lg:justify-end">
              <PeriodButtons
                activePeriod={activePeriodCategory}
                handleChangeTime={handleChangeTimeCategory}
              />
            </div>
          </div>
          <div className="mt-6 min-w-0 sm:mt-8">
            <div className="w-full min-w-0 max-w-full overflow-hidden">
              {loading ? (
                <Loading type="chart" />
              ) : (
                <HighchartsReact
                  containerProps={{ className: 'overview-chart-host' }}
                  highcharts={Highcharts}
                  options={optionsChart2}
                  ref={categoryChartRef}
                />
              )}
            </div>
            {!loading && categorySeries.length > 0 ? (
              <div className="mt-4 overflow-x-auto">
                <div className="flex w-max items-center gap-4 pb-2">
                  {categorySeries.map((seriesItem, index) => {
                    const isHidden = hiddenCategorySeries[index];
                    return (
                      <button
                        key={`${seriesItem.name}-${index}`}
                        type="button"
                        onClick={() => handleCategoryLegendToggle(index)}
                        className={`flex items-center gap-2 text-xs font-medium transition-opacity ${
                          isHidden ? 'opacity-40' : 'opacity-100'
                        }`}
                        aria-pressed={!isHidden}
                      >
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: seriesItem.color }}
                        />
                        <span className="whitespace-nowrap text-slate-600 dark:text-slate-300">
                          {seriesItem.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="col-span-12">
          <Table
            loading={loading}
            type="simple"
            tableData={tableData}
            customWidthClassName="w-24"
            customColumnsHeader={{
              author_name: 'Developers',
              git_effort:
                displayMode === DISPLAY_MODES.INVESTMENT
                  ? 'Estimated Cost Without AI ($)'
                  : 'Baseline Effort (REV)',
              commit: 'Commit',
              avg_git_effort:
                displayMode === DISPLAY_MODES.INVESTMENT
                  ? 'Avg. Estimated Cost Without AI ($)'
                  : 'Avg. Effort Value',
              insertions: 'Insertions',
              deletions: 'Deletions',
              file: 'file',
              lastDate: 'Last Date'
            }}
            sorting={[{ id: 'git_effort', desc: true }]}
            diffButton={false}
            hasLink={false}
            //dataKey={repos}
          />
        </div>
      </div>
    </>
  );
};

export default Overview;
